import { Box, Button, Popup, Table, TableRow, TopBar } from '@sam/components';
import { TableHeader, TableKey } from '@sam/components/src/Table/Table.types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Right, updateSchambeckUserTableConfig, updateUsersForRole, useData } from 'shared';
import { NotAllowed } from '../../components/NotAllowed';
import { useUser } from '../../components/UserContext';
import { convertUserRolesIntoTableEntries, isUserAllowedToDo } from '../../utils/user/User.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

const UserRoleOverview: React.FC = () => {
  const { user, axios, updateContextUser } = useUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: loadedRoles } = useData('ROLES_ALL', {
    config: { fallbackData: [] },
  });
  const [roleToUpdate, setRoleToUpdate] = useState<string>();

  /**
   * Handler to update the right for all users of a role after the role has changed
   */
  const handleUserUpdate = (): void => {
    if (!axios || !roleToUpdate) return;
    updateUsersForRole(axios, roleToUpdate).then(() => setRoleToUpdate(undefined));
  };
  // Holds tableRows for userRoles
  const rows: TableRow[] = useMemo(
    (): TableRow[] => convertUserRolesIntoTableEntries(loadedRoles, navigate, setRoleToUpdate),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loadedRoles],
  );

  return isUserAllowedToDo(user.right, Right.USERROLE_OVERVIEW) ? (
    <>
      <TopBar title={t('pages.userRole.overview.headline')}>
        {isUserAllowedToDo(user.right, Right.USERROLE_CREATE) && (
          <IconComponent type={IconType.ADD} onClick={() => navigate('/roles/create')} />
        )}
      </TopBar>
      <Popup
        isOpen={!!roleToUpdate}
        title={t('pages.userRole.overview.updateTitle')}
        onClose={() => setRoleToUpdate(undefined)}
        children={<p>{t('pages.userRole.overview.updateContent')}</p>}
        buttons={[
          <Button value={t('general.buttons.cancel')} onClick={() => setRoleToUpdate(undefined)} />,
          <Button value={t('general.buttons.confirm')} onClick={() => handleUserUpdate()} />,
        ]}
      />
      <Box>
        <Table
          tableConfig={user.tableConfig?.get(TableKey.USER_ROLE_OVERVIEW)}
          updateTableConfig={(updatedHeader) => {
            updateSchambeckUserTableConfig(
              axios,
              user,
              TableKey.USER_ROLE_OVERVIEW,
              updatedHeader,
            ).then((updatedUser) => updatedUser && updateContextUser(updatedUser));
          }}
          rows={rows}
          header={
            t('pages.userRole.overview.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </>
  ) : (
    <NotAllowed neccessaryRight={Right.USERROLE_OVERVIEW} />
  );
};

export default UserRoleOverview;
